/**
 * Dossier de transmission (Excel .xlsx) — même contrat de données que le PDF
 * (`TransmissionDossier.js`), donc mêmes chiffres que le bloc « Équilibre ».
 *
 * 4 feuilles : Synthèse, Répartition, Inventaire, À estimer.
 * L'indicateur fiscal (droits) n'est inclus que si `data.includeTax` est vrai.
 * xlsx (SheetJS) est chargé dynamiquement pour ne pas alourdir le bundle.
 */
export async function generateTransmissionDossierXLSX({ data, t, locale = "fr-FR" }) {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();
  const x = (k, o) => t(`succession.dossier.xls.${k}`, o);

  // 1. Synthèse
  const summary = [
    [t("succession.dossier.title")],
    [],
    [t("succession.dossier.client"), data.clientName || ""],
    [t("succession.dossier.date"), new Date().toLocaleDateString(locale)],
    [],
    [t("succession.balance.collectionValue"), data.collectionValue],
    [t("succession.balance.assigned"), data.assignedValue],
    [t("succession.balance.unassigned"), data.unassignedValue],
    [x("coverage"), data.coveragePct],
  ];
  if (data.includeTax) {
    summary.push([t("succession.tax.totalLabel"), data.totalTax]);
  }
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(summary),
    x("sheetSummary")
  );

  // 2. Répartition par héritier
  if (data.heirs.length > 0) {
    const head = [x("colHeir"), x("colShareValue"), x("colSharePct")];
    if (data.includeTax) head.push(x("colRegime"), x("colDuties"));
    const rows = [
      head,
      ...data.heirs.map((h) => {
        const r = [h.name, h.value, h.pct];
        if (data.includeTax) r.push(h.regimeLabel, h.exempt ? 0 : h.tax);
        return r;
      }),
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(rows),
      x("sheetHeirs")
    );
  }

  // 3. Inventaire des œuvres
  const invHead = [
    x("colNum"),
    x("colTitle"),
    x("colArtist"),
    t("succession.dossier.value"),
    x("colDate"),
    t("succession.dossier.heirs"),
    t("succession.dossier.provenance"),
    t("succession.dossier.location"),
    x("colToEstimate"),
  ];
  const invRows = [
    invHead,
    ...data.items.map((it) => [
      it.n,
      it.title,
      it.artist,
      it.value,
      it.dated ? it.dateStr : "",
      it.heirsStr || "",
      it.provenance || "",
      it.location || "",
      it.dated ? "" : x("yes"),
    ]),
  ];
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(invRows),
    x("sheetInventory")
  );

  // 4. Œuvres à faire estimer
  if (data.undatedItems.length > 0) {
    const teRows = [
      [x("colNum"), x("colTitle"), x("colArtist")],
      ...data.undatedItems.map((it, i) => [i + 1, it.title, it.artist]),
    ];
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(teRows),
      x("sheetToEstimate")
    );
  }

  const safe = (data.clientName || "artkiv").replace(/[^a-zA-Z0-9_-]/g, "_");
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `dossier_transmission_${safe}_${stamp}.xlsx`);
}
